import EditIcon from '@mui/icons-material/Edit';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import React, { Fragment, useState, type JSX } from 'react';
import { Mandant, Vorgang, VorgangRechnungspositionEintrag } from '../types';
import { Euro } from './common/Euro';
import Formular from './common/Formular';
import { RechnungspositonenEditierenDialog } from './dialog/RechnungspositonenEditierenDialog';
import { ermittleRechnungspositionEinheit, ermittleRechnungspositionLabel, ermittleRechnungssummeMwSt } from '../shared/rechnungHelper';

type Props = {
  readonly titel: string | JSX.Element;
  readonly vorgang: Vorgang;
  readonly mandant: Mandant | null;
  readonly rechnungspositionen: VorgangRechnungspositionEintrag[];
  readonly isLoading: boolean;
  readonly geaendetePositionen: boolean;
  readonly aktualisiereRechnungspositionen: (rechnungspositionen: VorgangRechnungspositionEintrag[]) => void;
  readonly canEdit?: boolean;
};

export function Rechnungspositionen({
  titel,
  vorgang,
  mandant,
  rechnungspositionen,
  isLoading,
  geaendetePositionen,
  aktualisiereRechnungspositionen,
  canEdit = true
}: Props) {
  const [openEditDialog, setOpenEditDialog] = useState(false);

  const ermittleSumme = (rechnungspositionen: VorgangRechnungspositionEintrag[]) => {
    let sum = 0;
    rechnungspositionen.forEach((rp) => (sum += !rp.geloescht ? rp.summe / 100 : 0));
    return sum;
  };

  const nettoSumme = ermittleSumme(rechnungspositionen);
  const mwStSumme = ermittleRechnungssummeMwSt(rechnungspositionen);
  return (
    <Grid size={{ xs: 12, lg: 6 }}>
      <Formular ueberschrift={titel}>
        <Grid container>
          <>
            <Grid size={4} borderBottom="1px solid grey">
              Position
            </Grid>
            <Grid size={2} textAlign="right" borderBottom="1px solid grey">
              Anzahl
            </Grid>
            <Grid size={3} textAlign="right" borderBottom="1px solid grey">
              Wert
            </Grid>
            <Grid size={3} textAlign="right" borderBottom="1px solid grey">
              Summe
            </Grid>
            {rechnungspositionen?.map((rp, index) => {
              let className = '';
              if (rp.geloescht) {
                className = 'deletedRow';
              } else if (rp.editiert) {
                className = 'editedRow';
              }
              return (
                <Fragment key={index}>
                  <Grid size={4} display="flex" alignItems="center" className={className}>
                    {ermittleRechnungspositionLabel(rp.titel) ?? rp.titel}{' '}
                    {rp.editiert && (
                      <Tooltip title="Position wurde manuell editiert">
                        <EditIcon color="disabled" className="editIcon" />
                      </Tooltip>
                    )}
                  </Grid>
                  <Grid size={2} textAlign="right" className={className}>
                    {rp.anzahl} {ermittleRechnungspositionEinheit(rp.einheit)}
                  </Grid>
                  <Grid size={3} textAlign="right" className={className}>
                    <Euro value={rp.wert / 100} />
                  </Grid>
                  <Grid size={3} textAlign="right" className={className}>
                    <Euro value={rp.summe / 100} />
                  </Grid>
                </Fragment>
              );
            })}
            <Grid size={9} borderTop="1px solid grey" marginTop="0.5rem">
              Nettobetrag:
              <br />
              MwSt.-Betrag:
              <br />
              <b>Gesamtbetrag:</b>
            </Grid>
            <Grid size={3} textAlign="right" borderTop="1px solid grey" marginTop="0.5rem">
              <Euro value={nettoSumme} />
              <br />
              <Euro value={mwStSumme} />
              <br />
              <b>
                <Euro value={nettoSumme + mwStSumme} />
              </b>
            </Grid>
          </>
          {canEdit && (
            <Grid size={12} display="flex" justifyContent="flex-end">
              <IconButton color="primary" onClick={() => setOpenEditDialog(true)}>
                <EditIcon />
              </IconButton>
            </Grid>
          )}
        </Grid>
      </Formular>
      {openEditDialog && (
        <RechnungspositonenEditierenDialog
          open={openEditDialog}
          onClose={() => setOpenEditDialog(false)}
          aktualisiereRechnungspositionen={(rechnungspositionen) => {
            aktualisiereRechnungspositionen(rechnungspositionen);
            setOpenEditDialog(false);
          }}
          isLoading={isLoading}
          rechnungsparameter={rechnungspositionen ?? []}
          mandant={mandant}
          gutachtenart={vorgang.gutachtenart}
          reihenfolgeAenderbar={false}
          geaendetePositionen={geaendetePositionen}
        />
      )}
    </Grid>
  );
}
